import { HardHat, MessageSquareText } from "lucide-react";

type Props = {
  /** The technician wrote a comment. */
  hasComment: boolean;
  /** Files the technician sent; 0 hides the number. */
  attachmentCount: number;
  /**
   * `onDark` is the board pill — white on a translucent black, next to `AttachmentBadge`. `muted`
   * is the grey inline mark the work-order table uses beside its paperclip.
   */
  tone?: "onDark" | "muted";
  /** Native tooltip — the comment text itself, where the caller has it. */
  title?: string;
  className?: string;
};

/**
 * "The technician sent something back" — a hard hat, so it cannot be mistaken for the office's
 * paperclip: the two collections are different things (see `TechnicianAttachmentSchema`) and the
 * dispatcher's question is "did the worker report anything?", not "how many files are there?".
 *
 * Renders nothing when there is nothing to say.
 */
export const TechnicianFeedbackBadge = ({
  hasComment,
  attachmentCount,
  tone = "onDark",
  title,
  className,
}: Props) => {
  if (!hasComment && attachmentCount <= 0) return null;

  const label = [
    hasComment ? "Kommentar vom Handwerker" : null,
    attachmentCount > 0
      ? `${attachmentCount} ${attachmentCount === 1 ? "Datei" : "Dateien"} vom Handwerker`
      : null,
  ]
    .filter(Boolean)
    .join(", ");

  const toneClass =
    tone === "onDark"
      ? "h-4 min-w-4 px-1 rounded-full bg-black/25 text-white text-[9px] font-bold leading-none"
      : "text-gray-600 text-xs";

  return (
    <span
      className={`inline-flex items-center gap-0.5 ${toneClass} ${className ?? ""}`}
      title={title ?? label}
      aria-label={label}
    >
      <HardHat className={tone === "onDark" ? "w-3 h-3" : "w-3.5 h-3.5"} strokeWidth={2.5} aria-hidden />
      {hasComment && (
        <MessageSquareText
          className={tone === "onDark" ? "w-3 h-3" : "w-3.5 h-3.5"}
          strokeWidth={2.5}
          aria-hidden
        />
      )}
      {attachmentCount > 0 && <span>{attachmentCount}</span>}
    </span>
  );
};
